import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../services/api'

const AuthContext = createContext({
  user: null,
  role: null,
  accessToken: null,
  refreshToken: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  updateUser: () => {},
  refreshUser: async () => {}
})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage synchronously
    try {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        const parsed = JSON.parse(storedUser)
        return parsed
      }
    } catch (e) {
      console.error("AUTH: Error parsing initial user:", e)
    }
    return null
  })
  const [role, setRole] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        return JSON.parse(storedUser)?.role
      }
    } catch (e) {}
    return null
  })
  const [accessToken, setAccessToken] = useState(() => {
    return localStorage.getItem('access_token') || localStorage.getItem('token') || null
  })
  const [refreshToken, setRefreshToken] = useState(() => {
    return localStorage.getItem('refresh_token') || null
  })
  const [loading, setLoading] = useState(false) // Start with false since we loaded from localStorage

  // Set axios auth header on mount if token exists
  useEffect(() => {
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  }, [accessToken])

  // Fetch current user when token is available
  useEffect(() => {
    let isMounted = true

    async function loadUser() {
      if (!accessToken) {
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      try {
        const response = await api.get('/auth/me')

        const parsedUser = response.data.user || response.data;

        if (isMounted) {
          setUser(parsedUser)
          setRole(parsedUser?.role)
          // Also update localStorage with the fresh data from server
          localStorage.setItem('user', JSON.stringify(parsedUser))
        }
      } catch (error) {
        console.error('Error loading user:', error)
        // Token invalid, clear it
        if (isMounted) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          setAccessToken(null)
          setRefreshToken(null)
          setUser(null)
          setRole(null)
          delete api.defaults.headers.common['Authorization']
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadUser()

    return () => {
      isMounted = false
    }
  }, [accessToken])

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password })

      // Backend returns { token, user: {...} } format
      const token = response.data.token || response.data.access_token;
      const refresh_token = response.data.refresh_token;
      const userData = response.data.user || response.data;

      // Store tokens - use 'token' key for consistency
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('access_token', token); // Also store as access_token for compatibility
      }
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Store user object
      localStorage.setItem('user', JSON.stringify(userData));
      
      setAccessToken(token)
      setRefreshToken(refresh_token)
      setUser(userData)
      setRole(userData?.role)

      // Set token in axios defaults
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      return { success: true }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      }
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setAccessToken(null)
    setRefreshToken(null)
    setUser(null)
    setRole(null)
    delete api.defaults.headers.common['Authorization']
  }, [])

  // Update user data (e.g., after avatar upload)
  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates }
      localStorage.setItem('user', JSON.stringify(updated))
      return updated
    })
  }, [])

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    if (!accessToken) return
    try {
      const response = await api.get('/auth/me')
      const userData = response.data.user || response.data
      setUser(userData)
      setRole(userData?.role)
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (error) {
      console.error('Error refreshing user:', error)
    }
  }, [accessToken])

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        accessToken,
        refreshToken,
        loading,
        login,
        logout,
        updateUser,
        refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
